import { useState, useRef, useEffect } from 'react';

const tracks = [
  '/sounds/retrogame1.mp3',
  '/sounds/retrogame2.mp3',
  '/sounds/retrogame3.mp3',
  '/sounds/retrogame4.mp3',
  '/sounds/retrogame5.mp3',
  '/sounds/retrogame7.mp3',
  '/sounds/retrogame8.mp3',
  '/sounds/retrolightning.mp3',
  '/sounds/bitflip.wav',
  '/sounds/retrowinner.wav'
];

export default function useAudioPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const audioRef = useRef(null);
  // Consecutive failed-to-load music tracks; lets the player skip a broken
  // file without looping forever when the whole playlist is unreachable.
  const failedTracksRef = useRef(0);
  const flipSoundRef = useRef(null);
  const winnerSoundRef = useRef(null);
  const wheelButtonSoundRef = useRef(null);

  // Music loads lazily (preload "none") so a multi-MB track isn't fetched
  // until the user actually plays; sound effects stay preloaded for instant
  // playback.
  const makeTrackAudio = (index) => {
    const audio = new Audio();
    audio.preload = 'none';
    audio.src = tracks[index];
    return audio;
  };

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = makeTrackAudio(currentTrackIndex);
    }
    if (!flipSoundRef.current) {
      flipSoundRef.current = new Audio('/sounds/bitflip.wav');
      flipSoundRef.current.load();
    }
    if (!winnerSoundRef.current) {
      // Same winning jingle the Bit Flip app streams, so wins sound
      // identical across the games.
      winnerSoundRef.current = new Audio('https://media.satoshiadvance.com/audio/retrowinner.wav');
      winnerSoundRef.current.load();
    }
    if (!wheelButtonSoundRef.current) {
      wheelButtonSoundRef.current = new Audio('/sounds/RetroWheelSpin2.mp3');
      wheelButtonSoundRef.current.load();
    }

    return () => {
      audioRef.current && audioRef.current.pause();
      flipSoundRef.current && flipSoundRef.current.pause();
      winnerSoundRef.current && winnerSoundRef.current.pause();
      wheelButtonSoundRef.current && wheelButtonSoundRef.current.pause();
    };
  }, [currentTrackIndex]);

  // Auto-advance when a track finishes, and skip past tracks that fail to
  // load (404, decode error) so one bad file doesn't silently kill the
  // music. Stops after one full failed cycle. Re-attached whenever the
  // track changes because playNextTrack swaps in a fresh Audio element.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => playNextTrack();
    const handleError = () => {
      failedTracksRef.current += 1;
      if (failedTracksRef.current >= tracks.length) return;
      playNextTrack();
    };
    const handleCanPlay = () => {
      failedTracksRef.current = 0;
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrackIndex, isPlaying]);

  const playNextTrack = () => {
    setCurrentTrackIndex(prev => {
      const nextIndex = (prev + 1) % tracks.length;
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current = makeTrackAudio(nextIndex);
      if (isPlaying) {
        audioRef.current.play().catch(err => console.error('Failed to play next track:', err));
      }
      return nextIndex;
    });
  };

  const togglePlayPause = () => {
    if (!audioRef.current) {
      audioRef.current = makeTrackAudio(currentTrackIndex);
    }

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => console.error('Audio play failed:', err));
    }
    setIsPlaying(prev => !prev);
  };

  return {
    isPlaying,
    playNextTrack,
    togglePlayPause,
    flipSoundRef,
    winnerSoundRef,
    wheelButtonSoundRef
  };
}
